import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { useWalletAdapterBridge } from '../../lib/walletBridge'
import { useAdapterSignAndSubmit } from '../../lib/walletAdapter'
import { useSplBalance } from '../../hooks/useSplBalance'
import { deserializeV0 } from '../../lib/tx'
import { buildLpTx, fetchRaydiumPools, fetchOrcaPools } from '../../api/lp'
import type { LpState, LpBuildTxRequest, RaydiumPool, OrcaPool } from '../../types/lp'
import type { StageAllocation } from '../../types/strategy'
import { MINTS } from '../../api/trade'
import TokenIcon from '../common/TokenIcon'
import TradeResultBanner, { type TradeResult } from '../trade/TradeResultBanner'
import SuggestionCard from '../shared/SuggestionCard'

const RAYDIUM_URL = 'https://raydium.io/portfolio/'
const ORCA_URL    = 'https://www.orca.so/portfolio'

type Protocol     = 'raydium' | 'orca'
type Operation    = 'create' | 'add' | 'remove' | 'close'
type RangePreset  = 'tight' | 'moderate' | 'wide' | 'full'

const PRESET_PCT: Record<RangePreset, [number, number]> = {
  tight:    [1, 1],
  moderate: [5, 5],
  wide:     [15, 15],
  full:     [50, 50],
}

interface Props {
  positions: LpState[]
  lpStage?: StageAllocation | null
  onClose: () => void
  prefillProvider?: Protocol
  prefillPositionMint?: string
  onRefresh?: () => void
}

function usd(v: string | number) {
  const n = typeof v === 'string' ? parseFloat(v) : v
  if (!Number.isFinite(n)) return '—'
  return '$' + Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })
}

function pct(v: string | number, d = 2) {
  const n = typeof v === 'string' ? parseFloat(v) : v
  return (n * 100).toFixed(d) + '%'
}

export default function LpManagePanel({
  positions, lpStage, onClose, prefillProvider, prefillPositionMint, onRefresh,
}: Props) {
  const { portfolioId } = useAuth()
  const { signAndSubmit, publicKey, connected, friendlyTxError } = useAdapterSignAndSubmit()
  useWalletAdapterBridge()

  const isProtocolLocked = !!prefillProvider
  const [protocol, setProtocol]     = useState<Protocol>(prefillProvider ?? 'raydium')
  const [operation, setOperation]   = useState<Operation>(prefillPositionMint ? 'add' : 'create')

  const protocolPositions = positions.filter(p => p.provider === protocol)
  const [selectedPos, setSelectedPos] = useState<LpState | null>(null)

  const [pools, setPools]           = useState<(RaydiumPool | OrcaPool)[]>([])
  const [selectedPool, setSelectedPool] = useState<(RaydiumPool | OrcaPool) | null>(null)
  const [poolsLoading, setPoolsLoading] = useState(false)
  const [poolsError, setPoolsError]     = useState<string | null>(null)

  const [rangePreset, setRangePreset] = useState<RangePreset>('moderate')
  const [lowerPct, setLowerPct]     = useState(5)
  const [upperPct, setUpperPct]     = useState(5)

  const [solAmount, setSolAmount]   = useState('')
  const [removePct, setRemovePct]   = useState(50)

  const [result, setResult]         = useState<TradeResult>({ kind: 'idle' })

  const splSol = useSplBalance(publicKey, MINTS.SOL)

  // Pre-fill from row click
  useEffect(() => {
    if (!prefillPositionMint) return
    const pos = positions.find(p => p.positionMint === prefillPositionMint)
    if (pos) setSelectedPos(pos)
  }, [prefillPositionMint, positions])

  // Load pools when on Create tab
  useEffect(() => {
    if (operation !== 'create') return
    let cancelled = false
    setPoolsLoading(true)
    setPools([])
    setSelectedPool(null)
    setPoolsError(null)
    const load = async () => {
      try {
        const data = protocol === 'raydium'
          ? await fetchRaydiumPools(MINTS.SOL, MINTS.USDC)
          : await fetchOrcaPools(MINTS.SOL, MINTS.USDC)
        if (!cancelled) {
          setPools(data.slice(0, 3))
          setSelectedPool(data[0] ?? null)
        }
      } catch (e) {
        if (!cancelled) setPoolsError(String((e as any)?.message ?? e))
      } finally { if (!cancelled) setPoolsLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [protocol, operation])

  // Reset position when protocol changes
  useEffect(() => {
    if (!isProtocolLocked) setSelectedPos(null)
  }, [protocol, isProtocolLocked])

  const currentPrice = selectedPos
    ? parseFloat(selectedPos.currentPrice)
    : parseFloat(String((selectedPool as any)?.price ?? 0)) || 0

  const poolId = operation === 'create'
    ? ((selectedPool as RaydiumPool)?.id ?? (selectedPool as OrcaPool)?.address ?? '')
    : (selectedPos?.poolId ?? '')

  const estimatedUsdc = (() => {
    const sol = parseFloat(solAmount)
    if (!sol || !currentPrice) return null
    return (sol * currentPrice).toFixed(2)
  })()

  const applyPreset = (p: RangePreset) => {
    setRangePreset(p)
    const [lo, hi] = PRESET_PCT[p]
    setLowerPct(lo)
    setUpperPct(hi)
  }

  const maxSol = () => {
    const raw = Number(splSol.decimal) - 0.01
    if (raw > 0) setSolAmount(raw.toFixed(6))
  }

  const handleSubmit = useCallback(async () => {
    if (!portfolioId || !publicKey) return
    if (portfolioId !== publicKey.toBase58()) {
      setResult({ kind: 'error', message: 'Connected wallet does not match your session' })
      return
    }
    if (!poolId) {
      setResult({ kind: 'error', message: 'Select a pool to continue' })
      return
    }
    if (operation !== 'create' && !selectedPos) {
      setResult({ kind: 'error', message: 'Select a position to continue' })
      return
    }

    const solLamports = (operation === 'create' || operation === 'add') ? (() => {
      const n = parseFloat(solAmount)
      return n > 0 ? Math.floor(n * 1e9).toString() : null
    })() : null

    if ((operation === 'create' || operation === 'add') && !solLamports) {
      setResult({ kind: 'error', message: 'Enter a SOL amount to deposit' })
      return
    }

    const req: LpBuildTxRequest = {
      protocol,
      operation,
      poolId,
      walletPubkey: publicKey.toBase58(),
      positionMint: selectedPos?.positionMint,
      lowerPct:     operation === 'create' ? lowerPct : undefined,
      upperPct:     operation === 'create' ? upperPct : undefined,
      baseAmountLamports: solLamports ?? undefined,
      removePct:    operation === 'remove' ? removePct : undefined,
      currentPrice: currentPrice ? String(currentPrice) : undefined,
    }

    setResult({ kind: 'pending', label: 'Building transaction…' })
    try {
      const { txBase64 } = await buildLpTx(portfolioId, req)
      const tx = deserializeV0(txBase64)
      setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
      const submitted = await signAndSubmit(tx)
      const opLabel    = operation.charAt(0).toUpperCase() + operation.slice(1)
      const protoLabel = protocol === 'raydium' ? 'Raydium' : 'Orca'
      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `${opLabel} liquidity on ${protoLabel} confirmed — position updates in ~30s`,
      })
      setSolAmount('')
      setTimeout(() => onRefresh?.(), 5_000)
    } catch (e) {
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }, [portfolioId, publicKey, protocol, operation, poolId, selectedPos, solAmount, lowerPct, upperPct, removePct, currentPrice, signAndSubmit, friendlyTxError, onRefresh])

  const handleHarvest = useCallback(async () => {
    if (!portfolioId || !publicKey || !selectedPos) return
    if (portfolioId !== publicKey.toBase58()) {
      setResult({ kind: 'error', message: 'Connected wallet does not match your session' })
      return
    }
    const req: LpBuildTxRequest = {
      protocol,
      operation:    'harvest',
      poolId:       selectedPos.poolId,
      walletPubkey: publicKey.toBase58(),
      positionMint: selectedPos.positionMint,
      currentPrice: selectedPos.currentPrice,
    }
    setResult({ kind: 'pending', label: 'Building harvest transaction…' })
    try {
      const { txBase64 } = await buildLpTx(portfolioId, req)
      const tx = deserializeV0(txBase64)
      setResult({ kind: 'pending', label: 'Waiting for wallet signature…' })
      const submitted = await signAndSubmit(tx)
      setResult({
        kind: 'success',
        signature: submitted.signature,
        summary: `Collected ${usd(selectedPos.totalPendingYieldUsd)} in fees — balance updates in ~30s`,
      })
      setTimeout(() => onRefresh?.(), 5_000)
    } catch (e) {
      setResult({ kind: 'error', message: friendlyTxError(e) })
    }
  }, [portfolioId, publicKey, protocol, selectedPos, signAndSubmit, friendlyTxError, onRefresh])

  const submitLabel = result.kind === 'pending'
    ? 'Working…'
    : !connected
      ? 'Connect wallet'
      : `Sign & ${operation.charAt(0).toUpperCase() + operation.slice(1)} on ${protocol === 'raydium' ? 'Raydium' : 'Orca'}`

  const totalValue = protocolPositions.reduce((s, p) => s + parseFloat(p.lpValueUsd), 0)

  const hasGap  = lpStage && lpStage.gapUsd != null && Math.abs(lpStage.gapUsd) > 1
  const isOver  = hasGap && lpStage!.gapUsd! < 0
  const gapAbs  = hasGap ? Math.abs(lpStage!.gapUsd!) : 0
  const gapPct  = hasGap && lpStage!.amountUsd > 0 ? Math.abs(lpStage!.gapUsd!) / lpStage!.amountUsd : 0

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="relative w-full max-w-xl bg-[#131a2a] border-l border-fringe-strong shadow-2xl flex flex-col h-full animate-slide-in-right overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-fringe-strong shrink-0">
          <div className="w-7 h-7 rounded-lg bg-cyan-400/10 flex items-center justify-center text-cyan-400">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4">
              <path d="M2 12c2-4 4-6 6-6s4 4 6 4 4-4 6-4"/>
              <path d="M2 17c2-3 4-5 6-5s4 3 6 3 4-3 6-3"/>
            </svg>
          </div>
          <span className="text-sm font-bold text-ink">Manage Liquidity</span>
          {protocolPositions.length > 0 && (
            <span className="text-xs text-ink-muted font-medium">
              {protocolPositions.length} position{protocolPositions.length !== 1 ? 's' : ''} · {usd(totalValue)}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <a href={RAYDIUM_URL} target="_blank" rel="noopener noreferrer"
              className="text-[10px] text-cyan-400 hover:underline">Raydium ↗</a>
            <span className="text-gray-600">·</span>
            <a href={ORCA_URL} target="_blank" rel="noopener noreferrer"
              className="text-[10px] text-violet-400 hover:underline">Orca ↗</a>
            <button onClick={onClose} className="ml-1 text-gray-500 hover:text-ink transition-colors p-1">
              <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Gap recommendation */}
        {hasGap && (
          <div className="px-4 pt-3 shrink-0">
            <SuggestionCard
              action={isOver ? 'Remove liquidity from pool' : 'Add liquidity to pool'}
              amount={`${isOver ? '−' : '+'}${usd(gapAbs)}`}
              pctChange={`(${pct(gapPct, 1)})`}
              isPositive={!isOver}
              description={isOver
                ? 'Your LP position exceeds the recommended size. Reducing it limits impermanent loss exposure and frees capital for higher-priority stages.'
                : 'Your LP position is below the recommended allocation. Adding liquidity captures more trading fees and reward APR while the position remains in range.'}
              impacts={[
                { label: 'Stage APY', value: (lpStage!.yieldOrCostApr > 0 ? '+' : '') + pct(lpStage!.yieldOrCostApr), color: 'text-accent-green' },
                { label: 'Daily earn', value: (lpStage!.dailyYieldOrCostUsd > 0 ? '+' : '') + usd(lpStage!.dailyYieldOrCostUsd), color: 'text-accent-green' },
                ...(lpStage!.riskLevel ? [{ label: 'Risk', value: lpStage!.riskLevel,
                  color: lpStage!.riskLevel === 'LOW' ? 'text-accent-green' : lpStage!.riskLevel === 'MEDIUM' ? 'text-amber-400' : 'text-red-400' }] : []),
              ]}
            />
          </div>
        )}

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">

          {/* Protocol toggle */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Protocol</div>
            <div className="flex rounded-lg overflow-hidden border border-fringe">
              {(['raydium', 'orca'] as Protocol[]).map(p => (
                <button
                  key={p}
                  disabled={isProtocolLocked}
                  onClick={() => { setProtocol(p); setOperation('create'); setResult({ kind: 'idle' }) }}
                  className={`flex-1 py-1.5 text-xs font-semibold transition-colors disabled:cursor-default
                    ${protocol === p
                      ? p === 'raydium' ? 'bg-cyan-400/20 text-cyan-300' : 'bg-violet-400/20 text-violet-300'
                      : 'text-gray-500 hover:text-ink'}`}
                >
                  {p === 'raydium' ? 'Raydium CLMM' : 'Orca Whirlpool'}
                </button>
              ))}
            </div>
          </div>

          {/* Operation toggle */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Operation</div>
            <div className="flex rounded-lg overflow-hidden border border-fringe">
              {(['create', 'add', 'remove', 'close'] as Operation[]).map(op => (
                <button
                  key={op}
                  onClick={() => { setOperation(op); setResult({ kind: 'idle' }) }}
                  className={`flex-1 py-1.5 text-xs font-semibold capitalize transition-colors
                    ${operation === op ? 'bg-accent-blue/20 text-accent-blue' : 'text-gray-500 hover:text-ink'}`}
                >
                  {op}
                </button>
              ))}
            </div>
          </div>

          {/* Your existing positions (Create tab) — browsing pools shouldn't hide
              positions you already hold; let people deposit/withdraw straight
              from here instead of having to know to switch to the Add/Remove tab. */}
          {operation === 'create' && protocolPositions.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
                Your {protocol === 'raydium' ? 'Raydium' : 'Orca'} position{protocolPositions.length !== 1 ? 's' : ''}
              </div>
              {protocolPositions.map(pos => {
                const [sym0, sym1] = pos.pair.split('/')
                return (
                  <div
                    key={pos.positionMint}
                    className="rounded-lg border border-fringe bg-surface-hover px-3 py-2.5 mb-1.5 last:mb-0"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <div className="flex -space-x-1">
                          <TokenIcon symbol={sym0} mint={pos.token0Mint} size={14}/>
                          <TokenIcon symbol={sym1} mint={pos.token1Mint} size={14}/>
                        </div>
                        <span className="font-semibold text-ink">{pos.pair}</span>
                        <span className={`w-1.5 h-1.5 rounded-full ${pos.inRange ? 'bg-accent-green' : 'bg-accent-red'}`}/>
                        <span className="text-gray-500">{pos.inRange ? 'IN' : 'OUT'}</span>
                      </div>
                      <span className="font-semibold text-ink">{usd(pos.lpValueUsd)}</span>
                    </div>
                    <div className="text-gray-500 mb-2">
                      ${parseFloat(pos.priceLower).toFixed(4)} → ${parseFloat(pos.priceUpper).toFixed(4)}
                      {parseFloat(pos.totalPendingYieldUsd) > 0 && (
                        <span className="ml-2 text-accent-green">Fees: {usd(pos.totalPendingYieldUsd)}</span>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => { setOperation('add'); setSelectedPos(pos); setResult({ kind: 'idle' }) }}
                        className="flex-1 py-1.5 text-[11px] font-semibold rounded bg-accent-blue/15 text-accent-blue hover:bg-accent-blue/25 transition-colors"
                      >
                        Deposit
                      </button>
                      <button
                        type="button"
                        onClick={() => { setOperation('remove'); setSelectedPos(pos); setResult({ kind: 'idle' }) }}
                        className="flex-1 py-1.5 text-[11px] font-semibold rounded bg-accent-red/15 text-accent-red hover:bg-accent-red/25 transition-colors"
                      >
                        Withdraw
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Pool picker (Create only) */}
          {operation === 'create' && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
                {protocolPositions.length > 0 ? 'Open a new position' : 'Pool'}
              </div>
              {poolsLoading && (
                <div className="animate-pulse space-y-1.5">
                  {[0, 1, 2].map(i => <div key={i} className="h-14 bg-surface rounded-lg"/>)}
                </div>
              )}
              {poolsError && (
                <div className="text-xs text-accent-red text-center py-5 bg-surface rounded-lg px-3">
                  Could not load {protocol === 'orca' ? 'Orca' : 'Raydium'} pools: {poolsError}
                </div>
              )}
              {!poolsLoading && !poolsError && pools.length === 0 && (
                <div className="text-xs text-gray-500 text-center py-5 bg-surface rounded-lg">
                  No pools found
                </div>
              )}
              {!poolsLoading && !poolsError && pools.map(pool => {
                const isRay = protocol === 'raydium'
                const p     = pool as any
                const id    = isRay ? p.id : (p.address ?? p.poolAddress ?? '')
                const symA  = isRay ? p.mintA?.symbol : (p.tokenA?.symbol ?? p.mintA?.symbol ?? '?')
                const symB  = isRay ? p.mintB?.symbol : (p.tokenB?.symbol ?? p.mintB?.symbol ?? '?')
                const tvl   = isRay ? (p.tvl ?? 0) : parseFloat(String(p.tvlUsdc ?? 0)) || 0
                const vol   = isRay ? (p.day?.volume ?? 0) : parseFloat(String(p.stats?.['24h']?.volume ?? 0)) || 0
                // Raydium v3 API returns feeApr already as a percentage (e.g. 18.45).
                // Orca returns feeRate in bps (not APR) and the 24h yield/TVL ratio
                // in stats['24h'].yieldOverTvl — annualise by × 365.
                const feeApr = isRay
                  ? (p.day?.feeApr ?? 0)
                  : (parseFloat(String(p.stats?.['24h']?.yieldOverTvl ?? 0)) * 365 * 100)
                const selId = (selectedPool as any)?.id ?? (selectedPool as any)?.address
                const selected = !!id && selId === id
                return (
                  <button
                    key={id || symA + symB}
                    onClick={() => setSelectedPool(pool)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-xs transition-colors mb-1.5 last:mb-0
                      ${selected ? 'border-accent-blue bg-accent-blue/15' : 'border-fringe bg-surface-hover hover:bg-surface-hover/80'}`}
                  >
                    <div className="text-left">
                      <div className="font-semibold text-ink">{symA}/{symB}</div>
                      <div className="text-gray-500 mt-0.5">TVL {usd(tvl)} · 24h Vol {usd(vol)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-accent-green font-medium">{feeApr.toFixed(2)}% APR</div>
                      {id && <div className="text-gray-500 text-[10px] mt-0.5 font-mono">{id.slice(0, 6)}…{id.slice(-4)}</div>}
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {/* Position picker (Add/Remove/Close) */}
          {operation !== 'create' && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">
                {protocol === 'raydium' ? 'Raydium' : 'Orca'} positions
              </div>
              {protocolPositions.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-6 bg-surface rounded-lg">
                  No {protocol} positions found
                </div>
              ) : protocolPositions.map(pos => {
                const [sym0, sym1] = pos.pair.split('/')
                const selected = selectedPos?.positionMint === pos.positionMint
                return (
                  <button
                    key={pos.positionMint}
                    onClick={() => setSelectedPos(selected ? null : pos)}
                    className={`w-full rounded-lg border px-3 py-2.5 text-left text-xs transition-colors mb-1.5 last:mb-0
                      ${selected ? 'border-accent-blue bg-accent-blue/15' : 'border-fringe bg-surface-hover hover:bg-surface-hover/80'}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <div className="flex -space-x-1">
                          <TokenIcon symbol={sym0} mint={pos.token0Mint} size={14}/>
                          <TokenIcon symbol={sym1} mint={pos.token1Mint} size={14}/>
                        </div>
                        <span className="font-semibold text-ink">{pos.pair}</span>
                        <span className={`w-1.5 h-1.5 rounded-full ${pos.inRange ? 'bg-accent-green' : 'bg-accent-red'}`}/>
                        <span className="text-gray-500">{pos.inRange ? 'IN' : 'OUT'}</span>
                      </div>
                      <span className="font-semibold text-ink">{usd(pos.lpValueUsd)}</span>
                    </div>
                    <div className="text-gray-500">
                      ${parseFloat(pos.priceLower).toFixed(4)} → ${parseFloat(pos.priceUpper).toFixed(4)}
                      {parseFloat(pos.totalPendingYieldUsd) > 0 && (
                        <span className="ml-2 text-accent-green">Fees: {usd(pos.totalPendingYieldUsd)}</span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {/* Range selector (Create only) */}
          {operation === 'create' && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Range</div>
              <div className="flex gap-1 mb-2">
                {(['tight', 'moderate', 'wide', 'full'] as RangePreset[]).map(p => (
                  <button
                    key={p}
                    onClick={() => applyPreset(p)}
                    className={`flex-1 py-1 text-[11px] font-medium rounded capitalize transition-colors
                      ${rangePreset === p ? 'bg-accent-blue/20 text-accent-blue' : 'bg-surface text-gray-500 hover:text-ink'}`}
                  >
                    {p === 'tight' ? '±1%' : p === 'moderate' ? '±5%' : p === 'wide' ? '±15%' : 'Full'}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {([['Lower −%', lowerPct, setLowerPct], ['Upper +%', upperPct, setUpperPct]] as const).map(([label, val, setter]) => (
                  <div key={label} className="bg-surface-hover rounded-lg p-2">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={val}
                        onChange={e => { (setter as (v: number) => void)(Number(e.target.value) || 0); setRangePreset('wide') }}
                        className="w-full bg-transparent text-sm font-semibold text-ink outline-none"
                      />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                  </div>
                ))}
              </div>
              {currentPrice > 0 && (
                <div className="mt-1.5 text-[11px] text-gray-500 bg-surface rounded p-2">
                  Range: <span className="text-ink">${(currentPrice * (1 - lowerPct / 100)).toFixed(4)}</span>
                  {' → '}
                  <span className="text-ink">${(currentPrice * (1 + upperPct / 100)).toFixed(4)}</span> USDC/SOL
                </div>
              )}
            </div>
          )}

          {/* Amount input (Create/Add) */}
          {(operation === 'create' || operation === 'add') && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Deposit</div>
              <div className="rounded-lg border border-fringe bg-surface-hover p-3">
                <label className="mb-1.5 flex items-center justify-between text-[10px] uppercase tracking-wider text-gray-500">
                  <span>SOL amount</span>
                  {!splSol.raw.isZero() && (
                    <button type="button" onClick={maxSol} className="text-accent-purple hover:underline">
                      MAX {parseFloat(splSol.decimal).toFixed(4)}
                    </button>
                  )}
                </label>
                <div className="flex items-center gap-2">
                  <TokenIcon symbol="SOL" mint={MINTS.SOL} size={18}/>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.0"
                    value={solAmount}
                    onChange={e => setSolAmount(e.target.value)}
                    className="flex-1 bg-transparent text-lg font-semibold text-ink outline-none"
                  />
                </div>
                {estimatedUsdc && (
                  <div className="mt-1 text-[11px] text-gray-500">
                    ≈ <span className="text-ink">{estimatedUsdc}</span> USDC at current price
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Remove % (Remove only, position selected) */}
          {operation === 'remove' && selectedPos && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Remove percentage</div>
              <div className="bg-surface-hover rounded-lg p-3">
                <div className="flex gap-1.5 mb-2">
                  {[25, 50, 75, 99].map(v => (
                    <button
                      key={v}
                      onClick={() => setRemovePct(v)}
                      className={`flex-1 py-1 rounded text-[11px] font-medium transition-colors
                        ${removePct === v ? 'bg-accent-purple/25 text-ink' : 'text-gray-500 hover:text-ink'}`}
                    >
                      {v}%
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={1}
                    max={99}
                    value={removePct}
                    onChange={e => setRemovePct(Number(e.target.value))}
                    className="flex-1 accent-accent-purple"
                  />
                  <span className="w-10 text-right text-sm font-semibold text-ink">{removePct}%</span>
                </div>
                <div className="mt-1.5 text-[11px] text-gray-500">
                  Withdrawing ≈ <span className="text-ink">{usd(parseFloat(selectedPos.lpValueUsd) * removePct / 100)}</span>{' '}
                  of {usd(selectedPos.lpValueUsd)} position
                </div>
              </div>
            </div>
          )}

          {/* Close confirmation */}
          {operation === 'close' && selectedPos && (
            <div className="rounded-lg border border-accent-red/30 bg-accent-red/10 p-3 text-xs space-y-1">
              <div className="font-semibold text-accent-red">Close entire position</div>
              <div className="text-gray-400">Value: <span className="text-ink">{usd(selectedPos.lpValueUsd)}</span></div>
              {parseFloat(selectedPos.totalPendingYieldUsd) > 0 && (
                <div className="text-gray-400">
                  Pending fees collected: <span className="text-accent-green">{usd(selectedPos.totalPendingYieldUsd)}</span>
                </div>
              )}
              <div className="text-gray-500 pt-0.5">
                All liquidity is removed and fees collected in a single transaction.
              </div>
            </div>
          )}

          {/* Harvest CTA (position selected, has claimable yield — both Raydium and Orca supported) */}
          {operation !== 'create' && selectedPos && parseFloat(selectedPos.totalPendingYieldUsd) > 0 && (
            <button
              type="button"
              disabled={result.kind === 'pending'}
              onClick={handleHarvest}
              className="w-full rounded-lg border border-accent-green/40 bg-accent-green/10 py-2 text-xs font-semibold text-accent-green hover:bg-accent-green/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Collect {usd(selectedPos.totalPendingYieldUsd)} in fees (keep position open)
            </button>
          )}

          {/* Submit */}
          <button
            type="button"
            disabled={result.kind === 'pending'}
            onClick={handleSubmit}
            className={`w-full rounded-lg py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50
              ${operation === 'remove' || operation === 'close'
                ? 'bg-accent-red/15 border border-accent-red/50 text-accent-red hover:bg-accent-red/25'
                : 'bg-accent-purple text-white hover:bg-accent-purple/80'}`}
          >
            {submitLabel}
          </button>

          <TradeResultBanner result={result} onDismiss={() => setResult({ kind: 'idle' })}/>
        </div>
      </div>
    </div>
  )
}
